import React from 'react'
import type { FileRejection } from 'react-dropzone'

import {
  TextBlockEllipsis,
  Toasts,
  Upload,
  UploadedAsset,
  useMediaQueryContext,
} from '@fourthwall/components'
import { fileToBase64 } from '@fourthwall/utils/dist/file'
import { useFormikContext } from '@fourthwall/utils/dist/formik'
import { useCurrentShop } from '@modules/Dashboard/hooks/useCurrentShop'
import type { SocialImageTypeParams } from '@modules/Dashboard/pages/Settings/pages/General/queries/useUpdateShopRendererMutation'
import { getRejectionErrors } from '@utils/dropzone'

import type { FormValues } from '../../General.types'
import * as S from './SocialImage.styled'

export const SocialImage = () => {
  const media = useMediaQueryContext()
  const { values, setFieldValue } = useFormikContext<FormValues>()
  const { currentShop } = useCurrentShop()

  const setImageTypeValue = (type: SocialImageTypeParams) => {
    setFieldValue('socialImageType', type)
  }

  const handleFileDrop = async (files?: File[]) => {
    if (files?.[0]) {
      const base64 = await fileToBase64(files[0])

      setFieldValue('socialImage', base64)
      setImageTypeValue('New')
    }
  }

  const handleRemove = async () => {
    setFieldValue('socialImage', undefined)
    setImageTypeValue('Remove')
  }

  const handleFileDropRejected = (rejectedFiles: FileRejection[]) => {
    getRejectionErrors(rejectedFiles, {
      'file-too-large': 'File is larger than 1 megabyte',
    }).forEach((rejection) => {
      Toasts.notify(rejection)
    })
  }

  const renderUpload = () => {
    const image = values.socialImage

    if (image) {
      return (
        <UploadedAsset id="1" src={image} onRemove={handleRemove}>
          <S.Image src={image} />
        </UploadedAsset>
      )
    }

    return (
      <Upload
        accept={{
          'image/jpeg': ['.jpg', '.jpeg'],
          'image/png': ['.png'],
        }}
        title={media.tablet ? 'Drop image or browse' : 'Browse'}
        maxSize={1048576}
        onFileDrop={handleFileDrop}
        onFileDropRejected={handleFileDropRejected}
      />
    )
  }

  return (
    <S.Wrapper>
      <S.Name>Social sharing image</S.Name>
      <S.Description>
        <p>Image that appears when you share your site on social media.</p>
        <p>Recommended size: 1200 x 630 px</p>
      </S.Description>
      <S.Container>
        <S.UploadContainer>{renderUpload()}</S.UploadContainer>
        <S.SocialInfoContainer>
          <S.Name>
            <TextBlockEllipsis maxLine={1} text={values.name} />
          </S.Name>
          <span>
            <TextBlockEllipsis maxLine={1} text={`https://${currentShop?.domain}`} />
          </span>
          <S.SocialDescription>
            <TextBlockEllipsis maxLine={2} text={values.description} />
          </S.SocialDescription>
        </S.SocialInfoContainer>
      </S.Container>
    </S.Wrapper>
  )
}
